import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MessageCircle, GraduationCap } from 'lucide-react';
export function AboutSection() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.15,
        ease: 'easeOut'
      }
    })
  };
  return <section id="sobre-nosotros" className="relative py-24 md:py-32 overflow-hidden" data-id="element-12">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-dark" data-id="element-13" />
      <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIuNSIvPjwvZz48L3N2Zz4=')]" data-id="element-14" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-15">
        {/* Main 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-16">
          {/* LEFT: Two photos, no overlap */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 order-1" data-id="element-17">
            {/* Photo 1 — café client */}
            <motion.div custom={0} variants={fadeUp} className="relative aspect-[3/4] rounded-[20px] overflow-hidden shadow-2xl shadow-black/60 border border-gold/20" data-id="element-18">
              <img src="https://cdn.magicpatterns.com/uploads/rdXdADyGWz2qQjnkhV7FGD/WhatsApp_Image_2026-05-06_at_3.55.38_PM.jpg" alt="Cliente disfrutando un café mientras recibe un corte en Luciano Barber Shop" className="w-full h-full object-cover object-center" data-id="element-19" />
            </motion.div>

            {/* Photo 2 — barber cutting */}
            <motion.div custom={1} variants={fadeUp} className="relative aspect-[3/4] rounded-[20px] overflow-hidden shadow-2xl shadow-black/60 border border-white/5 sm:mt-10" data-id="element-20">
              <img src="https://cdn.magicpatterns.com/uploads/o7hXEDRXX7xUbf1AwiCYom/WhatsApp_Image_2026-05-02_at_8.02.37_AM.jpg" alt="Barbero cortando el cabello en Luciano Barber Shop" className="w-full h-full object-cover object-[center_30%]" data-id="element-21" />
            </motion.div>

            {/* Decorative gold accents */}
            <div className="absolute -bottom-4 -left-4 w-20 h-20 border-l-2 border-b-2 border-gold/30 rounded-bl-3xl pointer-events-none" data-id="element-22" />
            <div className="absolute -top-4 -right-4 w-20 h-20 border-r-2 border-t-2 border-gold/30 rounded-tr-3xl pointer-events-none" data-id="element-23" />
          </motion.div>

          {/* RIGHT: Text content */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-80px'
        }} className="order-2" data-id="element-24">
            {/* Eyebrow */}
            <motion.span custom={0} variants={fadeUp} className="inline-block text-gold text-xs font-bold tracking-[0.25em] uppercase mb-4" data-id="element-25">
              Sobre Nosotros
            </motion.span>

            {/* Title */}
            <motion.h2 custom={1} variants={fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-26">
              Más de 8 años
              <br data-id="element-27" />
              elevando tu estilo.
            </motion.h2>

            {/* Mission statement */}
            <motion.p custom={2} variants={fadeUp} className="text-lg md:text-xl text-gold/90 font-medium mb-8 leading-relaxed" data-id="element-28">
              Nuestra misión: que salgas seguro, prolijo y con un look que te
              represente.
            </motion.p>

            {/* Paragraphs */}
            <motion.div custom={3} variants={fadeUp} className="space-y-4 mb-8" data-id="element-29">
              <p className="text-gray-400 leading-relaxed" data-id="element-30">
                Luciano Barber es más que un corte. Es un espacio cómodo donde
                recibís atención personalizada, asesoramiento según tu estilo y
                un ambiente pensado para que disfrutes la experiencia.
              </p>
              <p className="text-gray-400 leading-relaxed" data-id="element-31">
                Trabajamos con técnica, terminación y más de 8 años de
                experiencia para que cada detalle cuente. Sin atajos, sin
                apuros.
              </p>
            </motion.div>

            {/* Academy highlight badge */}
            <motion.div custom={4} variants={fadeUp} className="bg-dark-card border-l-2 border-gold/60 rounded-r-xl p-5 mb-10" data-id="element-32">
              <div className="flex items-center gap-2 mb-2" data-id="element-33">
                <GraduationCap size={18} className="text-gold" data-id="element-34" />
                <span className="text-white font-bold text-sm uppercase tracking-wider" data-id="element-35">
                  Formamos Barberos/as
                </span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-36">
                También somos academia: formamos barberos/as con práctica y
                acompañamiento.{' '}
                <span className="text-white font-semibold" data-id="element-37">
                  +1000 alumnos exitosos.
                </span>
              </p>
            </motion.div>

            {/* CTAs */}
            <motion.div custom={5} variants={fadeUp} className="flex flex-col sm:flex-row gap-4" data-id="element-38">
              <a href="#sucursales" className="flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-dark font-bold py-3.5 px-7 rounded-full transition-all duration-300 transform hover:-translate-y-0.5 shadow-[0_0_20px_rgba(201,168,76,0.25)]" data-id="element-39">
                <Calendar size={18} data-id="element-40" />
                <span data-id="element-41">Reservar turno</span>
              </a>
              <a href="https://wa.me/59893472053" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-gold/40 text-gold hover:bg-gold/10 font-semibold py-3.5 px-7 rounded-full transition-all duration-300" data-id="element-42">
                <MessageCircle size={18} data-id="element-43" />
                <span data-id="element-44">Consultar cursos</span>
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom metrics band */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} className="mt-20 md:mt-28 bg-dark-card/80 border border-gold/10 rounded-2xl p-8 md:p-10" data-id="element-45">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-0" data-id="element-46">
            {/* Label */}
            <div className="md:w-1/3 text-center md:text-left md:pr-8 md:border-r md:border-gold/15" data-id="element-47">
              <p className="text-white font-bold text-lg md:text-xl" data-id="element-48">
                Lo que nos mueve
                <br className="hidden md:block" data-id="element-49" /> día a día:
              </p>
            </div>

            {/* Stats */}
            <div className="md:w-2/3 grid grid-cols-3 gap-6 md:gap-8 md:pl-10 w-full" data-id="element-50">
              {[{
              number: '8+',
              label: 'Años de experiencia'
            }, {
              number: '1000+',
              label: 'Alumnos formados'
            }, {
              number: '100%',
              label: 'Atención al detalle'
            }].map((stat, index) => <div key={index} className="text-center" data-id="element-51">
                  <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-gold mb-1" data-id="element-52">
                    {stat.number}
                  </p>
                  <p className="text-gray-400 text-xs md:text-sm" data-id="element-53">
                    {stat.label}
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}